import React, { useId } from 'react';

const TONE_FIELD = {
  light: 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-themePrimary focus:ring-themePrimary/30',
  dark: 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:border-violet-500 focus:ring-violet-500/30',
};
const TONE_LABEL = { light: 'text-slate-700', dark: 'text-slate-300' };
const TONE_HINT = { light: 'text-slate-500', dark: 'text-slate-500' };

const fieldClasses = (tone, hasError, hasLeftIcon) =>
  [
    'w-full border rounded-lg px-3 py-2.5 text-sm transition-colors',
    'focus:outline-none focus:ring-2',
    hasLeftIcon ? 'pl-10' : '',
    hasError ? 'border-red-400 focus:border-red-400 focus:ring-red-400/30' : TONE_FIELD[tone],
  ].join(' ');

export const Input = React.forwardRef(
  ({ label, error, hint, tone = 'light', leftIcon: LeftIcon, className = '', id, ...rest }, ref) => {
    const autoId = useId();
    const inputId = id || autoId;
    return (
      <div>
        {label && (
          <label htmlFor={inputId} className={['block text-sm font-medium mb-1.5', TONE_LABEL[tone]].join(' ')}>
            {label}
          </label>
        )}
        <div className="relative">
          {LeftIcon && (
            <LeftIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          )}
          <input
            ref={ref}
            id={inputId}
            className={[fieldClasses(tone, !!error, !!LeftIcon), className].join(' ')}
            {...rest}
          />
        </div>
        {error ? (
          <p className="text-xs text-red-600 mt-1">{error}</p>
        ) : hint ? (
          <p className={['text-xs mt-1', TONE_HINT[tone]].join(' ')}>{hint}</p>
        ) : null}
      </div>
    );
  }
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef(
  ({ label, error, hint, tone = 'light', className = '', id, ...rest }, ref) => {
    const autoId = useId();
    const inputId = id || autoId;
    return (
      <div>
        {label && (
          <label htmlFor={inputId} className={['block text-sm font-medium mb-1.5', TONE_LABEL[tone]].join(' ')}>
            {label}
          </label>
        )}
        <textarea ref={ref} id={inputId} className={[fieldClasses(tone, !!error, false), 'resize-none', className].join(' ')} {...rest} />
        {error ? (
          <p className="text-xs text-red-600 mt-1">{error}</p>
        ) : hint ? (
          <p className={['text-xs mt-1', TONE_HINT[tone]].join(' ')}>{hint}</p>
        ) : null}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef(
  ({ label, error, hint, tone = 'light', className = '', id, children, ...rest }, ref) => {
    const autoId = useId();
    const inputId = id || autoId;
    return (
      <div>
        {label && (
          <label htmlFor={inputId} className={['block text-sm font-medium mb-1.5', TONE_LABEL[tone]].join(' ')}>
            {label}
          </label>
        )}
        <select ref={ref} id={inputId} className={[fieldClasses(tone, !!error, false), className].join(' ')} {...rest}>
          {children}
        </select>
        {error ? (
          <p className="text-xs text-red-600 mt-1">{error}</p>
        ) : hint ? (
          <p className={['text-xs mt-1', TONE_HINT[tone]].join(' ')}>{hint}</p>
        ) : null}
      </div>
    );
  }
);
Select.displayName = 'Select';
